// First-touch attribution capture — stores the ORIGINAL external referrer
// and landing URL in localStorage on the visitor's very first pageview.
// Subsequent visits and internal navigation never overwrite the first-touch
// values, so if a visitor bounces around the site before applying we still
// know where they originally came from.
//
// Usage:
//   captureFirstTouch()                  — called from App.js on every route
//                                          change; only writes if empty
//   getFirstTouch() → { referrer, landing, ts, utm_* }
//                                        — read from PublisherApplication form

const KEY = "dmm_first_touch";

const readOrigin = () => {
  try {
    return window.location.origin;
  } catch {
    return "";
  }
};

const isExternalRef = (ref) => {
  if (!ref) return false;
  try {
    const u = new URL(ref);
    return u.origin && u.origin !== readOrigin();
  } catch {
    return false;
  }
};

const readUtms = () => {
  try {
    const params = new URLSearchParams(window.location.search);
    const out = {};
    ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"].forEach((k) => {
      const v = params.get(k);
      if (v) out[k] = v.slice(0, 120);
    });
    return out;
  } catch {
    return {};
  }
};

const readSourceParam = () => {
  try {
    const params = new URLSearchParams(window.location.search);
    // Accept `source` or `src` — analytics teams use both interchangeably
    const raw = (params.get("source") || params.get("src") || "").trim();
    if (!raw) return "";
    // Whitelist safe chars — analytics tags should never contain spaces or
    // punctuation beyond letters/digits/underscore/dash/dot.
    if (!/^[A-Za-z0-9_.:-]{1,120}$/.test(raw)) return "";
    return raw;
  } catch {
    return "";
  }
};

export const captureFirstTouch = () => {
  try {
    const sourceTag = readSourceParam();
    const existingRaw = localStorage.getItem(KEY);
    const existing = existingRaw ? JSON.parse(existingRaw) : null;

    // If an explicit ?source= tag is present, ALWAYS upgrade the stored
    // record — an intentional campaign tag beats whatever we recorded on
    // an earlier untagged visit. Otherwise, respect first-touch immutability.
    if (existing && !sourceTag) return;

    const ref = document.referrer || "";
    const utms = readUtms();

    let referrer = "";
    if (sourceTag) {
      referrer = sourceTag;
    } else if (isExternalRef(ref)) {
      referrer = ref;
    }

    const payload = {
      referrer,
      source_tag: sourceTag,
      landing:
        (existing && existing.landing) ||
        (window.location.pathname + window.location.search).slice(0, 500),
      ts: (existing && existing.ts) || new Date().toISOString(),
      ...utms,
    };
    localStorage.setItem(KEY, JSON.stringify(payload));
  } catch {
    /* silently swallow */
  }
};

export const getFirstTouch = () => {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
};
